/**
 * Read-only preview for a DocumentTemplate row.
 *
 * Three rendering modes, picked from the template's content:
 *
 *   1. **PDF / file**: ``file_url`` is set. Render an inline iframe
 *      pointing at the R2 (or local /uploads) URL so HR can scan the
 *      document without downloading it. A Download button still ships
 *      so they can grab a copy if needed.
 *
 *   2. **Text / markdown body**: ``content_template`` is set. Render
 *      it as a pre-wrapped block. We don't expand template variables
 *      ({{employee.first_name}} etc.) — this is a pre-instantiation
 *      preview, so showing the raw template is the right thing.
 *
 *   3. **Lenavio default placeholder**: neither file nor content. Show
 *      a friendly nudge pointing the admin at the edit pencil so they
 *      can upload their company's version.
 *
 * Sized large so a typical letter-size PDF is legible without forcing
 * the user to download. Modal sizing maxes at the viewport.
 */
import {
  AlertCircle,
  Download,
  ExternalLink,
  FileText,
  Pencil,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { FormRenderer } from "@/components/forms/FormRenderer";
import type { DocumentTemplate } from "@/types/document";

interface TemplatePreviewModalProps {
  template: DocumentTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  /** Optional — when provided, the placeholder mode shows an "Edit" CTA
   *  that closes the preview and opens the edit form. */
  onEdit?: (template: DocumentTemplate) => void;
}

export function TemplatePreviewModal({
  template,
  isOpen,
  onClose,
  onEdit,
}: TemplatePreviewModalProps) {
  if (!template) return null;

  const fileUrl = template.file_url ?? null;
  const content = template.content_template ?? null;
  const isForm = template.template_type === "form";
  const formSchema = isForm
    ? ((template.form_schema ?? []) as unknown as import("@/components/forms/types").FormField[])
    : [];
  const mode: "form" | "file" | "content" | "placeholder" = isForm
    ? "form"
    : fileUrl
      ? "file"
      : content && content.trim().length > 0
        ? "content"
        : "placeholder";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={template.name} size="lg">
      <div className="space-y-3">
        {/* Header row: type badge + filename + signature requirement */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="default">{template.type.replace(/_/g, " ")}</Badge>
          {template.default_requires_signature && (
            <Badge variant="info">Signature required</Badge>
          )}
          {template.file_name && (
            <span className="text-xs text-text-muted">
              {template.file_name}
            </span>
          )}
        </div>

        {template.description && (
          <p className="text-sm text-text-muted">{template.description}</p>
        )}

        {/* ── Form mode (digital form) ───────────────────────────── */}
        {mode === "form" && (
          <div className="rounded-md border border-border/40 bg-surface px-4 py-4 max-h-[70vh] overflow-y-auto">
            {formSchema.length === 0 ? (
              <p className="text-sm text-text-muted">
                This form has no fields yet. Edit the template to add fields.
              </p>
            ) : (
              <FormRenderer
                schema={formSchema}
                data={{}}
                onChange={() => undefined}
                disabled
              />
            )}
            <p className="text-[11px] text-text-muted mt-3 italic">
              Read-only preview. Employees see the same fields with sample
              data they can edit.
            </p>
          </div>
        )}

        {/* ── File mode (PDF / DOCX) ─────────────────────────────── */}
        {mode === "file" && fileUrl && (
          <div className="rounded-md border border-border/50 overflow-hidden bg-white">
            <iframe
              src={fileUrl}
              title={template.name}
              className="w-full h-[70vh]"
            />
          </div>
        )}

        {/* ── Text / markdown body ───────────────────────────────── */}
        {mode === "content" && content && (
          <div className="rounded-md border border-border/40 bg-surface px-4 py-3 max-h-[70vh] overflow-y-auto">
            <pre className="text-sm whitespace-pre-wrap font-sans text-text-primary">
              {content}
            </pre>
            <p className="text-[11px] text-text-muted mt-3 italic">
              Variables in <code>{"{{ … }}"}</code> are resolved at
              instantiation time and aren&rsquo;t expanded in this
              preview.
            </p>
          </div>
        )}

        {/* ── Empty / Lenavio placeholder ────────────────────────── */}
        {mode === "placeholder" && (
          <div className="rounded-md border border-warning/30 bg-warning/5 px-4 py-6 text-center">
            <FileText className="h-8 w-8 text-warning mx-auto mb-2" />
            <p className="text-sm text-text-primary font-medium">
              This is a Lenavio default template.
            </p>
            <p className="text-xs text-text-muted mt-1 max-w-md mx-auto">
              No file or content is attached yet. Upload your company&rsquo;s
              version by clicking the edit icon.
            </p>
            {template.description && (
              <div className="mt-4 mx-auto max-w-md text-left rounded-md border border-border/40 bg-surface px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-text-muted mb-1">
                  Sample description
                </p>
                <p className="text-xs text-text-muted">
                  {template.description}
                </p>
              </div>
            )}
            {onEdit && (
              <Button
                size="sm"
                className="mt-4"
                onClick={() => {
                  onClose();
                  onEdit(template);
                }}
              >
                <Pencil className="h-4 w-4" />
                Edit template
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="mt-4 flex justify-end gap-2 flex-wrap">
        {mode === "file" && fileUrl && (
          <>
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-accent hover:underline self-center mr-auto"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open in new tab
            </a>
            <Button
              variant="secondary"
              onClick={() => {
                // ``download`` attr nudges the browser to save the
                // file rather than render it inline. R2 / static
                // /uploads both honour this; a presigned URL would
                // also work.
                const a = document.createElement("a");
                a.href = fileUrl;
                a.download = template.file_name ?? template.name;
                a.rel = "noreferrer";
                document.body.appendChild(a);
                a.click();
                a.remove();
              }}
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </>
        )}
        {mode !== "file" && (
          <span className="self-center mr-auto inline-flex items-center gap-1 text-xs text-text-muted">
            <AlertCircle className="h-3.5 w-3.5" />
            Read-only preview
          </span>
        )}
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}

export default TemplatePreviewModal;
